(function (scope) {
  'use strict';

  if (scope.DELLA_BTU_CALCULATOR && scope.DELLA_BTU_CALCULATOR.engineVersion === 'della-sizing-engine-1.5.2') {
    if (typeof document !== 'undefined') scope.DELLA_BTU_CALCULATOR.initialize(document);
    return;
  }

  const CONFIG = Object.freeze({
    engineVersion: 'della-sizing-engine-1.5.2',
    baseBtuPerSqft: 20,
    minimumBaseLoadBtu: 5000,
    factors: {
      ceiling: { referenceHeightFt: 8, minimum: 0.9, maximum: 1.5 },
      designConditions: {
        indoorCoolingF: 75,
        indoorHeatingF: 70,
        coolingReferenceOutdoorF: 95,
        heatingReferenceOutdoorF: 30,
        coolingFactorPerDegree: 0.01,
        heatingFactorPerDegree: 0.01,
        coolingMinimum: 0.85,
        coolingMaximum: 1.2,
        heatingMinimum: 0.85,
        heatingMaximum: 1.6,
        fallbackByClimate: {
          cold: { summer: 89, winter: 0 },
          marine: { summer: 85, winter: 24 },
          mixed: { summer: 91, winter: 18 },
          hot_dry: { summer: 102, winter: 30 },
          hot_humid: { summer: 93, winter: 30 }
        },
        latentRatioByMoisture: { A: 0.08, B: 0, C: 0.04, humid: 0.08, default: 0.03 }
      },
      envelope: {
        minimum: 0.75,
        maximum: 1.5,
        insulationDelta: { excellent: -0.1, good: -0.05, standard: 0, poor: 0.1, very_poor: 0.2 },
        airtightnessDelta: { very_tight: -0.07, tight: -0.05, average: 0, drafty: 0.08, very_drafty: 0.15 },
        windowPerformanceDelta: { efficient: -0.05, standard: 0, older: 0.08 },
        exposedWallsDelta: { 1: -0.05, 2: 0, 4: 0.1 },
        ceilingBoundaryDelta: { conditioned: -0.04, attic: 0, exposed: 0.06 },
        floorBoundaryDelta: { conditioned: -0.03, slab: 0, unconditioned: 0.05 },
        windowRatio: {
          reference: 0.12,
          defaultEstimate: 0.15,
          sunroomEstimate: 0.35,
          slope: 0.6,
          minimumDelta: -0.06,
          maximumDelta: 0.18
        }
      },
      sun: { shaded: 0.9, average: 1, high_western: 1.1 },
      internalGains: {
        includedOccupants: 2,
        extraOccupantBtu: 600,
        kitchenBtu: { none: 0, no: 0, kitchenette: 2000, full: 4000, yes: 4000 }
      }
    },
    windowEstimates: {
      sizesSqft: { small: 6, typical: 12, large: 20 },
      patioDoorSqft: 40,
      maximumWindows: 100,
      maximumPatioDoors: 20
    },
    confidenceRanges: { high: 0.1, low: 0.25 },
    capacityBinsBtu: [9000, 12000, 18000, 24000, 36000],
    multiZoneCapacityBinsBtu: [38000, 41000, 42000, 45000, 48000, 55000],
    limits: {
      minimumAreaSqft: 20,
      maximumAreaSqft: 4000,
      smallLoadReviewBtu: 7000,
      singleZoneAutomaticMaxBtu: 36000,
      multiRoomReviewTotalBtu: 55000,
      singleRoomReviewAreaSqft: 1500,
      ceilingReviewAboveFt: 12
    },
    loadRoundingIncrementBtu: 100
  });

  const CLIMATE = scope.DELLA_BTU_CLIMATE_DATA || { types: {}, zones: {}, exact: {}, prefix3: {} };
  const STATE_CLIMATE = Object.freeze({
    ma: 'cold', ri: 'cold', nh: 'cold', me: 'cold', vt: 'cold', ct: 'cold', ny: 'cold',
    nj: 'mixed', pa: 'cold', de: 'mixed', md: 'mixed', dc: 'mixed', va: 'mixed', wv: 'mixed',
    nc: 'mixed', sc: 'mixed', ga: 'mixed', al: 'mixed', tn: 'mixed', fl: 'hot_humid',
    ky: 'mixed', ms: 'mixed', ar: 'mixed', oh: 'cold', in: 'cold', mi: 'cold', ia: 'cold',
    sd: 'cold', mn: 'cold', ne: 'cold', mo: 'mixed', wi: 'cold', nd: 'cold', mt: 'cold',
    wy: 'cold', id: 'cold', il: 'cold', ks: 'mixed', co: 'cold', la: 'hot_humid',
    ok: 'mixed', tx: 'hot_humid', nm: 'cold', ut: 'cold', wa: 'marine', nv: 'cold',
    az: 'hot_dry', ca: 'hot_dry', or: 'marine', hi: 'hot_humid', ak: 'cold'
  });
  const REGION_NAMES = Object.freeze({
    hot_humid: 'Region #1 (Red): Hot & Humid',
    hot_dry: 'Region #2 (Orange): Warm',
    mixed: 'Region #3 (Yellow): Mixed',
    marine: 'Region #4 (Green): Cool',
    cold: 'Region #5 (Blue): Cold'
  });
  const STATE_NAMES = Object.freeze({
    al: 'Alabama', ak: 'Alaska', az: 'Arizona', ar: 'Arkansas', ca: 'California', co: 'Colorado',
    ct: 'Connecticut', de: 'Delaware', dc: 'District of Columbia', fl: 'Florida', ga: 'Georgia',
    hi: 'Hawaii', id: 'Idaho', il: 'Illinois', in: 'Indiana', ia: 'Iowa', ks: 'Kansas',
    ky: 'Kentucky', la: 'Louisiana', me: 'Maine', md: 'Maryland', ma: 'Massachusetts',
    mi: 'Michigan', mn: 'Minnesota', ms: 'Mississippi', mo: 'Missouri', mt: 'Montana',
    ne: 'Nebraska', nv: 'Nevada', nh: 'New Hampshire', nj: 'New Jersey', nm: 'New Mexico',
    ny: 'New York', nc: 'North Carolina', nd: 'North Dakota', oh: 'Ohio', ok: 'Oklahoma',
    or: 'Oregon', pa: 'Pennsylvania', ri: 'Rhode Island', sc: 'South Carolina',
    sd: 'South Dakota', tn: 'Tennessee', tx: 'Texas', ut: 'Utah', vt: 'Vermont',
    va: 'Virginia', wa: 'Washington', wv: 'West Virginia', wi: 'Wisconsin', wy: 'Wyoming'
  });

  const initializedRoots = new WeakSet();
  const productCollectionRequests = new Map();
  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
  const formatNumber = (value) => Math.round(value).toLocaleString('en-US');
  const roundLoad = (value) => {
    const increment = CONFIG.loadRoundingIncrementBtu;
    return Math.round(value / increment) * increment;
  };

  function calculateRoomLoad(room, climateInput) {
    const sqft = Number(room && room.sqft);
    const ceilingHeight = Number(room && room.ceiling);
    if (!Number.isFinite(sqft) || sqft <= 0 || !Number.isFinite(ceilingHeight) || ceilingHeight <= 0) {
      throw new TypeError('A finite, positive room area and ceiling height are required.');
    }
    const factors = CONFIG.factors;
    const design = factors.designConditions;
    const climateRecord =
      climateInput && typeof climateInput === 'object' ? climateInput : { type: climateInput || 'mixed' };
    const climateType = climateRecord.type || 'mixed';
    const fallback = design.fallbackByClimate[climateType] || design.fallbackByClimate.mixed;
    const hasSummerOutdoor =
      climateRecord.summer !== null &&
      String(climateRecord.summer).trim() !== '' &&
      Number.isFinite(Number(climateRecord.summer));
    const hasWinterOutdoor =
      climateRecord.winter !== null &&
      String(climateRecord.winter).trim() !== '' &&
      Number.isFinite(Number(climateRecord.winter));
    const summerOutdoor = hasSummerOutdoor
      ? Number(climateRecord.summer)
      : fallback.summer;
    const winterOutdoor = hasWinterOutdoor
      ? Number(climateRecord.winter)
      : fallback.winter;
    const coolingDesign = clamp(
      1 + (summerOutdoor - design.coolingReferenceOutdoorF) * design.coolingFactorPerDegree,
      design.coolingMinimum,
      design.coolingMaximum
    );
    const heatingDesign = clamp(
      1 + (design.heatingReferenceOutdoorF - winterOutdoor) * design.heatingFactorPerDegree,
      design.heatingMinimum,
      design.heatingMaximum
    );
    const base = Math.max(CONFIG.minimumBaseLoadBtu, sqft * CONFIG.baseBtuPerSqft);
    const ceiling = clamp(
      ceilingHeight / factors.ceiling.referenceHeightFt,
      factors.ceiling.minimum,
      factors.ceiling.maximum
    );
    const requestedWindowArea = Number(room.windowArea);
    const windowArea = clamp(
      Number.isFinite(requestedWindowArea) ? requestedWindowArea : sqft * factors.envelope.windowRatio.defaultEstimate,
      0,
      sqft * 0.6
    );
    const windowRatio = windowArea / sqft;
    const windowRatioDelta = clamp(
      (windowRatio - factors.envelope.windowRatio.reference) * factors.envelope.windowRatio.slope,
      factors.envelope.windowRatio.minimumDelta,
      factors.envelope.windowRatio.maximumDelta
    );
    const exposedWalls = String(room.exposedWalls || 2);
    const ceilingBoundary = room.ceilingBoundary || 'attic';
    const floorBoundary = room.floorBoundary || 'slab';
    const windowPerformance = room.windowPerformance || 'standard';
    const envelope = clamp(
      1 +
        (factors.envelope.insulationDelta[room.insulation] || 0) +
        (factors.envelope.airtightnessDelta[room.airtightness] || 0) +
        (factors.envelope.windowPerformanceDelta[windowPerformance] || 0) +
        (factors.envelope.exposedWallsDelta[exposedWalls] || 0) +
        (factors.envelope.ceilingBoundaryDelta[ceilingBoundary] || 0) +
        (factors.envelope.floorBoundaryDelta[floorBoundary] || 0) +
        windowRatioDelta,
      factors.envelope.minimum,
      factors.envelope.maximum
    );
    const sun = factors.sun[room.sun] || factors.sun.average;
    const occupantCount = Number.isFinite(Number(room.occupants)) ? Math.max(0, Number(room.occupants)) : 2;
    const people =
      Math.max(0, occupantCount - factors.internalGains.includedOccupants) * factors.internalGains.extraOccupantBtu;
    const kitchenType = typeof room.kitchen === 'string' ? room.kitchen : room.kitchen ? 'full' : 'none';
    const kitchen = factors.internalGains.kitchenBtu[kitchenType] || 0;
    const envelopeCooling = base * ceiling * coolingDesign * envelope * sun;
    const sensibleCooling = envelopeCooling + people + kitchen;
    const zoneCode = String(climateRecord.zoneCode || '');
    const moistureCode = (zoneCode.match(/[ABC]$/) || [])[0] || (climateType === 'hot_humid' ? 'humid' : 'default');
    const latentRatio = design.latentRatioByMoisture[moistureCode] ?? design.latentRatioByMoisture.default;
    const latentCooling = envelopeCooling * latentRatio;
    const cooling = sensibleCooling + latentCooling;
    const heating = base * ceiling * heatingDesign * envelope;
    const governing = cooling >= heating ? 'cooling' : 'heating';
    const point = Math.max(cooling, heating);
    const flags = [];

    if (room.sunroom) flags.push('sunroom');
    if (ceilingHeight > CONFIG.limits.ceilingReviewAboveFt) flags.push('ceiling over 12 ft');
    if (sqft > CONFIG.limits.singleRoomReviewAreaSqft) flags.push('large open space');
    if (windowRatio >= 0.25) flags.push('high glass area');
    if (room.airtightness === 'drafty' || room.airtightness === 'very_drafty') flags.push('air leakage estimate');

    const confidence = flags.length ? 'low' : 'high';
    const range = CONFIG.confidenceRanges[confidence];

    return {
      base,
      ceiling,
      climate: coolingDesign,
      coolingDesign,
      heatingDesign,
      summerOutdoor,
      winterOutdoor,
      indoorCooling: design.indoorCoolingF,
      indoorHeating: design.indoorHeatingF,
      envelope,
      windowArea,
      windowRatio,
      windowRatioDelta,
      sun,
      people,
      kitchen,
      kitchenType,
      envelopeCooling,
      sensibleCooling,
      latentCooling,
      latentRatio,
      cooling,
      heating,
      governing,
      point,
      lower: point * (1 - range),
      upper: point * (1 + range),
      confidence,
      flags
    };
  }

  function selectCapacity(load) {
    if (load === null || String(load).trim() === '') {
      throw new TypeError('A finite, non-negative planning load is required.');
    }
    const numericLoad = Number(load);
    if (!Number.isFinite(numericLoad) || numericLoad < 0) {
      throw new TypeError('A finite, non-negative planning load is required.');
    }
    const normalizedLoad = roundLoad(numericLoad);
    if (normalizedLoad > CONFIG.limits.singleZoneAutomaticMaxBtu) {
      const capacity = CONFIG.multiZoneCapacityBinsBtu.find((candidate) => candidate >= normalizedLoad) || null;
      return { capacity, kind: capacity ? 'multi' : 'professional' };
    }
    if (normalizedLoad < CONFIG.limits.smallLoadReviewBtu) return { capacity: 9000, kind: 'below' };

    const capacity = CONFIG.capacityBinsBtu.find((candidate) => candidate >= normalizedLoad) || null;
    return { capacity, kind: capacity ? 'single' : 'professional' };
  }

  function resolveZipRecord(zip) {
    const normalized = String(zip || '').trim();
    if (!/^\d{5}$/.test(normalized)) return null;

    const encoded = CLIMATE.exact[normalized] || CLIMATE.prefix3[normalized.slice(0, 3)];
    if (!encoded) return null;

    const parts = encoded.split(':');
    const zoneCode = parts[0];
    const type = CLIMATE.types[parts[1]];
    const zone = CLIMATE.zones[zoneCode];
    if (!type || !zone) return null;

    return {
      zip: normalized,
      type,
      zoneCode,
      winter: zone.winter_design_temp_f,
      summer: zone.summer_design_temp_f,
      source: 'zip'
    };
  }

  function buildWhyItems(result, room, sizing) {
    const items = [];
    if (sizing.kind === 'multi') items.push('Planning loads above 36,000 BTU/h are routed to DELLA multi-zone options');
    if (sizing.kind === 'below') items.push('The planning load is under 9,000 BTU/h; 9K is DELLA’s smallest nominal size');
    if (sizing.kind === 'single') {
      items.push(`Estimated range ${formatNumber(roundLoad(result.lower))} to ${formatNumber(roundLoad(result.upper))} BTU/h`);
      const crossesBoundary = CONFIG.capacityBinsBtu.some(
        (capacity) => result.lower <= capacity && capacity < result.upper
      );
      if (crossesBoundary) {
        items.push('This estimate crosses a nominal-size boundary, so professional review is especially useful.');
      }
    }

    items.push(`Room size sets a base of ${formatNumber(result.base)} BTU/h`);
    items.push(
      `Climate estimate uses about ${result.summerOutdoor}°F outdoors for summer cooling and ${result.winterOutdoor}°F outdoors for winter heating`
    );
    if (result.ceiling !== 1) {
      items.push(
        `${room.ceiling} ft ceilings ${result.ceiling > 1 ? 'add' : 'trim'} ${Math.abs(
          Math.round((result.ceiling - 1) * 100)
        )}%`
      );
    }
    if (result.envelope !== 1) {
      items.push(
        `Envelope (insulation, air leakage, glass and room exposure) ${result.envelope > 1 ? 'adds' : 'trims'} ${Math.abs(
          Math.round((result.envelope - 1) * 100)
        )}%`
      );
    }
    if (result.sun !== 1) {
      items.push(
        `Sun exposure ${result.sun > 1 ? 'adds' : 'trims'} ${Math.abs(Math.round((result.sun - 1) * 100))}%`
      );
    }
    if (result.people) items.push(`Extra occupants add ${formatNumber(result.people)} BTU/h`);
    if (result.kitchen) {
      items.push(`${result.kitchenType === 'kitchenette' ? 'Kitchenette' : 'Full kitchen'} adds ${formatNumber(result.kitchen)} BTU/h`);
    }
    if (result.latentCooling) {
      items.push(`A simplified humidity adjustment adds ${formatNumber(roundLoad(result.latentCooling))} BTU/h to this cooling estimate`);
    }
    items.push(
      `${result.governing === 'heating' ? 'Heating' : 'Cooling'} is the governing planning load at ${formatNumber(
        roundLoad(result.point)
      )} BTU/h`
    );
    return items;
  }

  function createLegacyCalculator(root) {
    const field = (name) => root.querySelector(`[data-btu-field="${name}"]`);
    const error = (name) => root.querySelector(`[data-btu-error="${name}"]`);
    const resultBody = root.querySelector('[data-btu-result-body]');
    const climateChip = root.querySelector('[data-btu-climate-chip]');
    const statePicker = root.querySelector('[data-btu-state]');
    const sectionId = String(root.dataset.sectionId || 'calculator').replace(/[^a-zA-Z0-9_-]/g, '');
    const state = {
      climate: { type: 'mixed', zoneCode: null, winter: null, summer: null, source: 'default' },
      lastCompletionSignature: '',
      pendingCompletionSignature: '',
      completionTimer: null,
      hasInteracted: false,
      lastResolvedZip: ''
    };

    function track(eventName, data) {
      scope.dataLayer = scope.dataLayer || [];
      scope.dataLayer.push(Object.assign({ event: eventName }, data || {}));
    }

    function setError(name, visible, message) {
      const input = field(name);
      const messageElement = error(name);
      if (input) input.setAttribute('aria-invalid', visible ? 'true' : 'false');
      if (!messageElement) return;
      if (message) messageElement.textContent = message;
      messageElement.hidden = !visible;
    }

    function highlightRegion(type) {
      root.querySelectorAll('[data-btu-region]').forEach((button) => {
        const active = button.dataset.btuRegion === type;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function prepareMap() {
      const mapStates = root.querySelectorAll(
        '[data-btu-map-states] path:not(.dc), [data-btu-climate-map] .dccircle'
      );
      mapStates.forEach((mapState, index) => {
        const stateCode = Array.from(mapState.classList).find((className) => STATE_CLIMATE[className]);
        if (!stateCode) return;
        mapState.dataset.btuMapState = stateCode;
        mapState.setAttribute('tabindex', index === 0 ? '0' : '-1');
        mapState.setAttribute('role', 'button');
        mapState.setAttribute('aria-label', `${STATE_NAMES[stateCode]}: ${REGION_NAMES[STATE_CLIMATE[stateCode]]}`);
      });
    }

    function prepareRatings() {
      root
        .querySelectorAll('.loox-row[aria-label], .gr-stars[aria-label], .gr-star5[aria-label]')
        .forEach((rating) => rating.setAttribute('role', 'img'));
    }

    function setClimateChip(climate, mode) {
      if (!climateChip) return;
      climateChip.replaceChildren();
      const strong = document.createElement('b');
      strong.textContent = REGION_NAMES[climate.type];
      climateChip.appendChild(strong);

      if (mode === 'zip') {
        climateChip.append(
          ` · Estimate uses about ${climate.summer}°F outdoors for summer cooling and ${climate.winter}°F outdoors for winter heating (ZIP ${climate.zip})`
        );
      } else if (mode === 'default') {
        climateChip.append(
          ' set as the default. Enter your ZIP for a more precise climate estimate, or click your state for a regional fallback.'
        );
      } else {
        climateChip.append(' selected. Enter your ZIP Code for a more precise climate estimate.');
      }
    }

    function setRegion(type, options) {
      if (!REGION_NAMES[type]) return;
      const settings = options || {};
      state.climate = {
        type,
        zoneCode: null,
        winter: null,
        summer: null,
        source: settings.source || 'manual'
      };
      highlightRegion(type);
      setClimateChip(state.climate, settings.defaultRegion ? 'default' : 'manual');
      if (!settings.silent) renderResult();
      if (settings.track) track('btu_climate_region_selected', { climate: type, source: state.climate.source });
    }

    function resolveZip() {
      const zipInput = field('zip');
      if (!zipInput) return;
      const zip = zipInput.value.trim();

      if (!/^\d{5}$/.test(zip)) {
        setError('zip', zip.length > 0, 'Enter a valid 5-digit ZIP Code.');
        return;
      }

      const climate = resolveZipRecord(zip);
      if (!climate) {
        state.lastResolvedZip = '';
        setError('zip', true, 'We could not match that ZIP Code. Select a region or state instead.');
        return;
      }

      setError('zip', false, 'Enter a valid 5-digit ZIP Code.');
      state.climate = climate;
      highlightRegion(climate.type);
      setClimateChip(climate, 'zip');
      if (statePicker) statePicker.value = '';
      if (state.lastResolvedZip !== zip) {
        state.lastResolvedZip = zip;
        track('btu_zip_resolved', { climate: climate.type, zone: climate.zoneCode });
      }
      renderResult();
    }

    function readRoom() {
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      const sqftIsValid =
        Number.isFinite(sqft) &&
        sqft >= CONFIG.limits.minimumAreaSqft &&
        sqft <= CONFIG.limits.maximumAreaSqft;

      setError(
        'sqft',
        !sqftIsValid,
        `Enter a room area between ${CONFIG.limits.minimumAreaSqft} and ${formatNumber(
          CONFIG.limits.maximumAreaSqft
        )} square feet.`
      );
      if (!sqftIsValid) return null;

      return {
        roomType: field('roomType') ? field('roomType').value : '',
        sqft,
        ceiling: Number.parseFloat(field('ceiling').value),
        insulation: field('insulation').value,
        sun: field('sun').value,
        windowArea: Number.parseFloat(
          field('windowArea') ? field('windowArea').value : sqft * CONFIG.factors.envelope.windowRatio.defaultEstimate
        ),
        windowPerformance: field('windowPerformance') ? field('windowPerformance').value : 'standard',
        exposedWalls: field('exposedWalls') ? field('exposedWalls').value : '2',
        ceilingBoundary: field('ceilingBoundary') ? field('ceilingBoundary').value : 'attic',
        floorBoundary: field('floorBoundary') ? field('floorBoundary').value : 'slab',
        occupants: Number.parseInt(field('occupants').value, 10),
        airtightness: field('airtightness').value,
        kitchen: field('kitchen').value,
        sunroom: field('sunroom').value === 'yes'
      };
    }

    function resultLinkMarkup(query) {
      return `<a class="pill pill-navy" href="#" data-btu-match-query="${query}">Find Matching Products</a>`;
    }

    function resultLoadMarkup(load, whyItems) {
      const whyId = `BtuCalculator-${sectionId}-why`;
      const list = whyItems.map((item) => `<li>${item}</li>`).join('');
      return `<div class="r-load">Calculated Cooling Load: <b>${formatNumber(
        roundLoad(load)
      )} BTU/h</b> · <button type="button" class="r-why-btn" data-btu-why-toggle aria-expanded="false" aria-controls="${whyId}">Why this size?</button></div><ul class="r-why" id="${whyId}" data-btu-why hidden data-expanded="false">${list}</ul>`;
    }

    function applyResultLinks() {
      const searchBase = root.dataset.searchUrl || '/search';
      const label = root.dataset.matchingProductsLabel || 'Find Matching Products';
      root.querySelectorAll('[data-btu-match-query]').forEach((link) => {
        try {
          const url = new URL(searchBase, document.baseURI);
          url.searchParams.set('type', 'product');
          url.searchParams.set('q', link.dataset.btuMatchQuery || 'mini split');
          link.href = url.origin === window.location.origin ? `${url.pathname}${url.search}` : url.toString();
        } catch (_error) {
          link.href = '/search?type=product&q=mini%20split';
        }
        link.textContent = label;
      });

      root.querySelectorAll('[data-btu-multi-zone-link]').forEach((link) => {
        link.href = root.dataset.multiZoneUrl || '/collections/mini-split-multi-zone';
      });
    }

    function renderResult() {
      if (!resultBody) return;
      const room = readRoom();
      if (!room) {
        if (state.completionTimer) window.clearTimeout(state.completionTimer);
        state.pendingCompletionSignature = '';
        state.completionTimer = null;
        resultBody.innerHTML =
          '<div class="r-cap">Room details need attention</div><div class="r-big r-big-review">Check the highlighted field</div><div class="r-load">Enter values within the displayed range to update your recommendation.</div>';
        return;
      }

      const result = calculateRoomLoad(room, state.climate);
      const sizing = selectCapacity(result.point);
      const whyItems = buildWhyItems(result, room, sizing);
      let markup = '<div class="r-cap">Recommended Equipment Capacity</div>';

      if (sizing.kind === 'professional') {
        markup += '<div class="r-big r-big-review">Professional review recommended</div>';
        markup += `<div class="r-load">Calculated Cooling Load: <b>${formatNumber(
          roundLoad(result.point)
        )} BTU/h</b></div>`;
        markup +=
          '<div class="r-warn">This load exceeds DELLA’s largest single-zone capacity (36,000 BTU). Plan multiple systems with our support team.</div>';
        markup += resultLinkMarkup('multi zone mini split');
      } else {
        markup += `<div class="r-big">${formatNumber(sizing.capacity)} BTU</div>`;
        markup += resultLoadMarkup(result.point, whyItems);

        if (sizing.kind === 'multi') {
          markup +=
            '<div class="r-alt">This size comes as a multi-zone system. Plan two or more indoor units for even airflow.</div>';
          markup += resultLinkMarkup(`${sizing.capacity} BTU`);
        } else {
          if (result.flags.length) {
            markup += `<div class="r-warn">Flagged for review: ${result.flags.join(
              ', '
            )}. Confirm with DELLA support before purchase.</div>`;
          }
          markup += resultLinkMarkup(`${sizing.capacity} BTU mini split`);
          const headSize = sizing.capacity / 1000;
          markup += `<div class="r-alt">Cooling several rooms? <a href="#" data-btu-multi-zone-link>See multi-zone systems with a ${headSize}K head</a></div>`;
        }
      }

      resultBody.innerHTML = markup;
      applyResultLinks();

      if (!state.hasInteracted) return;

      const signature = [
        roundLoad(result.point),
        sizing.capacity || 'review',
        sizing.kind,
        state.climate.type
      ].join('|');
      if (signature === state.lastCompletionSignature) {
        if (state.completionTimer) window.clearTimeout(state.completionTimer);
        state.pendingCompletionSignature = '';
        state.completionTimer = null;
        return;
      }
      if (signature === state.pendingCompletionSignature) return;
      if (state.completionTimer) window.clearTimeout(state.completionTimer);
      state.pendingCompletionSignature = signature;
      state.completionTimer = window.setTimeout(() => {
        state.lastCompletionSignature = signature;
        state.pendingCompletionSignature = '';
        state.completionTimer = null;
        track('btu_calculator_completed', {
          calculator_type: 'single_room',
          calculated_load_btu: roundLoad(result.point),
          recommended_capacity_btu: sizing.capacity,
          climate: state.climate.type
        });
      }, 400);
    }

    function moveCarousel(name, direction) {
      const trackElement =
        name === 'reviews'
          ? root.querySelector('[data-btu-review-track]')
          : root.querySelector('[data-btu-product-track]');
      if (!trackElement) return;

      const distance = Math.max(240, trackElement.clientWidth * 0.8);
      const maximum = trackElement.scrollWidth - trackElement.clientWidth;
      if (name === 'reviews' && direction > 0 && trackElement.scrollLeft >= maximum - 20) {
        trackElement.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (name === 'reviews' && direction < 0 && trackElement.scrollLeft <= 20) {
        trackElement.scrollTo({ left: maximum, behavior: 'smooth' });
      } else {
        trackElement.scrollBy({ left: direction * distance, behavior: 'smooth' });
      }
    }

    root.addEventListener('submit', (event) => {
      if (event.target.matches('[data-btu-form]')) event.preventDefault();
      if (event.target.matches('[data-btu-product-form]')) {
        const card = event.target.closest('[data-product-handle]');
        track('btu_product_add_to_cart', { product_handle: card ? card.dataset.productHandle : '' });
      }
    });

    root.addEventListener('input', (event) => {
      const input = event.target.closest('[data-btu-field]');
      if (!input) return;
      state.hasInteracted = true;

      if (input.dataset.btuField === 'zip') {
        input.value = input.value.replace(/\D/g, '').slice(0, 5);
        if (input.value !== state.lastResolvedZip) state.lastResolvedZip = '';
        if (input.value.length < 5) setError('zip', false, 'Enter a valid 5-digit ZIP Code.');
        if (input.value.length === 5) resolveZip();
        return;
      }
      renderResult();
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-btu-field]');
      if (input && input.dataset.btuField !== 'zip') {
        state.hasInteracted = true;
        renderResult();
      }

      if (event.target.matches('[data-btu-state]')) {
        const stateCode = event.target.value;
        if (STATE_CLIMATE[stateCode]) {
          state.hasInteracted = true;
          setRegion(STATE_CLIMATE[stateCode], { source: 'state', track: true });
        }
      }
    });

    root.addEventListener(
      'blur',
      (event) => {
        if (event.target.matches('[data-btu-field="zip"]')) resolveZip();
      },
      true
    );

    root.addEventListener('click', (event) => {
      const regionButton = event.target.closest('[data-btu-region]');
      if (regionButton && root.contains(regionButton)) {
        state.hasInteracted = true;
        setRegion(regionButton.dataset.btuRegion, { source: 'legend', track: true });
        return;
      }

      const mapState = event.target.closest('[data-btu-map-state]');
      if (mapState && root.contains(mapState)) {
        const stateCode = mapState.dataset.btuMapState;
        if (stateCode) {
          state.hasInteracted = true;
          root.querySelectorAll('[data-btu-map-state]').forEach((candidate) => {
            candidate.setAttribute('tabindex', candidate === mapState ? '0' : '-1');
          });
          if (statePicker) statePicker.value = stateCode;
          setRegion(STATE_CLIMATE[stateCode], { source: 'map', track: true });
        }
        return;
      }

      const whyToggle = event.target.closest('[data-btu-why-toggle]');
      if (whyToggle && root.contains(whyToggle)) {
        const why = root.querySelector('[data-btu-why]');
        if (!why) return;
        const open = whyToggle.getAttribute('aria-expanded') !== 'true';
        whyToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        why.hidden = !open;
        why.dataset.expanded = open ? 'true' : 'false';
        why.classList.toggle('is-open', open);
        return;
      }

      const readToggle = event.target.closest('[data-btu-read-toggle]');
      if (readToggle && root.contains(readToggle)) {
        const body = root.querySelector('[data-btu-read-more]');
        if (!body) return;
        const open = readToggle.getAttribute('aria-expanded') !== 'true';
        readToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        readToggle.textContent = open ? 'Read less' : 'Read more';
        body.hidden = !open;
        body.dataset.expanded = open ? 'true' : 'false';
        return;
      }

      const reviewToggle = event.target.closest('[data-btu-review-toggle]');
      if (reviewToggle && root.contains(reviewToggle)) {
        const card = reviewToggle.closest('.gr-card');
        if (!card) return;
        const open = reviewToggle.getAttribute('aria-expanded') !== 'true';
        reviewToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        reviewToggle.textContent = open ? 'Show less' : 'Show more';
        card.classList.toggle('expanded', open);
        return;
      }

      const carouselButton = event.target.closest('[data-btu-carousel]');
      if (carouselButton && root.contains(carouselButton)) {
        moveCarousel(
          carouselButton.dataset.btuCarousel,
          carouselButton.dataset.direction === 'prev' ? -1 : 1
        );
        return;
      }

      const matchingLink = event.target.closest('[data-btu-match-query]');
      if (matchingLink && root.contains(matchingLink)) {
        track('btu_matching_product_clicked', { query: matchingLink.dataset.btuMatchQuery || '' });
        return;
      }

      const productLink = event.target.closest('[data-btu-product-track] a');
      if (productLink && root.contains(productLink)) {
        const card = productLink.closest('[data-product-handle]');
        track('btu_product_clicked', { product_handle: card ? card.dataset.productHandle : '' });
      }
    });

    root.addEventListener('keydown', (event) => {
      const mapState = event.target.closest('[data-btu-map-state]');
      if (!mapState || !root.contains(mapState)) return;

      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        const stateCode = mapState.dataset.btuMapState;
        state.hasInteracted = true;
        setRegion(STATE_CLIMATE[stateCode], { source: 'map_keyboard', track: true });
        return;
      }

      const navigationKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!navigationKeys.includes(event.key)) return;
      event.preventDefault();
      const mapStates = Array.from(root.querySelectorAll('[data-btu-map-state]'));
      const currentIndex = mapStates.indexOf(mapState);
      let nextIndex;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = mapStates.length - 1;
      else if (['ArrowRight', 'ArrowDown'].includes(event.key)) nextIndex = (currentIndex + 1) % mapStates.length;
      else nextIndex = (currentIndex - 1 + mapStates.length) % mapStates.length;

      mapStates.forEach((candidate, index) => candidate.setAttribute('tabindex', index === nextIndex ? '0' : '-1'));
      mapStates[nextIndex].focus();
    });

    prepareMap();
    prepareRatings();
    setRegion('mixed', { silent: true, defaultRegion: true, source: 'default' });
    renderResult();
  }

  function createCalculator(root) {
    const field = (name) => root.querySelector(`[data-btu-field="${name}"]`);
    const error = (name) => root.querySelector(`[data-btu-error="${name}"]`);
    const summary = (name) => root.querySelector(`[data-btu-summary="${name}"]`);
    const resultBody = root.querySelector('[data-btu-result-body]');
    const climateChip = root.querySelector('[data-btu-climate-chip]');
    const progressLabel = root.querySelector('[data-btu-progress-label]');
    const progressBar = root.querySelector('[data-btu-progress-bar]');
    const backButton = root.querySelector('[data-btu-back]');
    const nextButton = root.querySelector('[data-btu-next]');
    const navigation = root.querySelector('.btu-wizard__nav');
    const recommendations = root.querySelector('[data-btu-recommendations]');
    const recommendationsEyebrow = recommendations
      ? recommendations.querySelector('[data-btu-recommendations-eyebrow]') || recommendations.querySelector('.btu-recommendations__intro .btu-eyebrow')
      : null;
    const recommendationsHeading = recommendations
      ? recommendations.querySelector('[data-btu-recommendations-heading]') || recommendations.querySelector('.btu-recommendations__intro h2')
      : null;
    const recommendationsCopy = recommendations
      ? recommendations.querySelector('[data-btu-recommendations-copy]') || recommendations.querySelector('.btu-recommendations__intro > p:last-child')
      : null;
    const productTrack = recommendations ? recommendations.querySelector('[data-btu-product-track]') : null;
    const productStatus = recommendations ? recommendations.querySelector('[data-btu-product-status]') : null;
    const productFallback = recommendations ? recommendations.querySelector('[data-btu-product-fallback]') : null;
    const productViewAll = recommendations ? recommendations.querySelector('[data-btu-view-all]') : null;
    const productCarousel = recommendations ? recommendations.querySelector('[data-btu-product-carousel]') : null;
    const productCarouselControls = recommendations ? recommendations.querySelector('[data-btu-carousel-controls]') : null;
    const productPrevious = recommendations ? recommendations.querySelector('[data-btu-product-prev]') : null;
    const productNext = recommendations ? recommendations.querySelector('[data-btu-product-next]') : null;
    const productCarouselStatus = recommendations ? recommendations.querySelector('[data-btu-carousel-status]') : null;
    const originalRecommendationsEyebrow = recommendationsEyebrow ? recommendationsEyebrow.textContent : '';
    const originalRecommendationsHeading = recommendationsHeading ? recommendationsHeading.textContent : '';
    const originalRecommendationsCopy = recommendationsCopy ? recommendationsCopy.textContent : '';
    const sectionId = String(root.dataset.sectionId || 'calculator').replace(/[^a-zA-Z0-9_-]/g, '');
    const panels = Array.from(root.querySelectorAll('[data-btu-step-panel]'));
    let carouselFrame = 0;
    let carouselDrag = null;
    let carouselSuppressClick = false;
    let carouselStatusText = '';

    const roomLabels = Object.freeze({
      living: 'Living room',
      bedroom: 'Bedroom',
      office: 'Home office',
      garage: 'Garage',
      sunroom: 'Sunroom',
      other: 'Other'
    });
    const sunLabels = Object.freeze({
      shaded: 'Mostly shaded',
      average: 'Balanced light',
      high_western: 'Strong afternoon sun'
    });
    const insulationLabels = Object.freeze({
      excellent: 'Well insulated',
      good: 'Well insulated',
      standard: 'Average',
      poor: 'Limited / older',
      very_poor: 'Limited / older'
    });
    const ceilingLabels = Object.freeze({ 8: '8 ft', 10: '9–10 ft', 12: '11–12 ft' });
    const airtightnessLabels = Object.freeze({
      very_tight: 'Very tight',
      tight: 'Tight / newer',
      average: 'Average air leakage',
      drafty: 'Drafty',
      very_drafty: 'Very drafty'
    });
    const windowPerformanceLabels = Object.freeze({
      efficient: 'Low-E / efficient',
      standard: 'Double-pane',
      older: 'Older / single-pane'
    });
    const exposedWallsLabels = Object.freeze({ 1: '1 exterior wall', 2: '2 exterior walls', 4: '3–4 exterior walls' });
    const ceilingBoundaryLabels = Object.freeze({
      conditioned: 'Conditioned space above',
      attic: 'Insulated attic above',
      exposed: 'Exposed roof above'
    });
    const floorBoundaryLabels = Object.freeze({
      conditioned: 'Conditioned space below',
      slab: 'Slab / insulated below',
      unconditioned: 'Garage / crawlspace below'
    });
    const occupantLabels = Object.freeze({ 2: '1–2 people', 4: '3–4 people', 6: '5+ people' });
    const kitchenLabels = Object.freeze({ none: 'No kitchen', kitchenette: 'Kitchenette', full: 'Full kitchen' });
    const windowSizeLabels = Object.freeze({ small: 'Small', typical: 'Typical', large: 'Large / picture' });

    const state = {
      currentStep: 1,
      climate: { type: 'mixed', zoneCode: null, winter: null, summer: null, source: 'default' },
      lastCompletionSignature: '',
      lastResolvedZip: '',
      hasInteracted: false,
      completionTimer: null,
      roomTouched: false,
      windowAreaTouched: false,
      windowSizeTouched: false,
      locationResolved: false,
      result: null,
      productRequestToken: 0
    };

    function track(eventName, data) {
      scope.dataLayer = scope.dataLayer || [];
      scope.dataLayer.push(Object.assign({ event: eventName }, data || {}));
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function setError(name, visible, message) {
      const input = field(name);
      const messageElement = error(name);
      if (input) input.setAttribute('aria-invalid', visible ? 'true' : 'false');
      if (!messageElement) return;
      if (message) messageElement.textContent = message;
      messageElement.hidden = !visible;
    }

    function setClimateChip(climate, mode) {
      if (!climateChip) return;
      climateChip.replaceChildren();
      const strong = document.createElement('b');
      strong.textContent = mode === 'zip' ? 'Climate region detected' : REGION_NAMES[climate.type];
      climateChip.appendChild(strong);
      if (mode === 'zip') {
        climateChip.append(
          ` · ${REGION_NAMES[climate.type]} for ZIP ${climate.zip} · Estimate uses about ${climate.summer}°F outdoors for summer cooling and ${climate.winter}°F outdoors for winter heating`
        );
      }
    }

    function resolveZip(options) {
      const settings = options || {};
      const zipInput = field('zip');
      if (!zipInput) return false;
      const zip = zipInput.value.trim();

      if (!/^\d{5}$/.test(zip)) {
        setError('zip', settings.force || zip.length > 0, 'Enter a valid 5-digit ZIP Code.');
        state.locationResolved = false;
        updateSummary();
        return false;
      }

      const climate = resolveZipRecord(zip);
      if (!climate) {
        setError('zip', true, 'We could not match that ZIP Code. Check the number and try again.');
        state.locationResolved = false;
        updateSummary();
        return false;
      }

      setError('zip', false);
      state.climate = climate;
      state.locationResolved = true;
      setClimateChip(climate, 'zip');
      if (state.lastResolvedZip !== zip) {
        state.lastResolvedZip = zip;
        track('btu_zip_resolved', { climate: climate.type, zone: climate.zoneCode });
      }
      updateSummary();
      renderResult();
      return true;
    }

    function readRoom() {
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      const sqftIsValid =
        Number.isFinite(sqft) &&
        sqft >= CONFIG.limits.minimumAreaSqft &&
        sqft <= CONFIG.limits.maximumAreaSqft;

      setError(
        'sqft',
        !sqftIsValid,
        `Enter a room area between ${CONFIG.limits.minimumAreaSqft} and ${formatNumber(
          CONFIG.limits.maximumAreaSqft
        )} square feet.`
      );
      if (!sqftIsValid) return null;

      return {
        roomType: field('roomType').value,
        sqft,
        ceiling: Number.parseFloat(field('ceiling').value),
        insulation: field('insulation').value,
        sun: field('sun').value,
        windowArea: effectiveWindowArea(),
        windowMethod: field('windowMethod').value,
        windowCount: Number.parseInt(field('windowCount').value, 10),
        windowSize: field('windowSize').value,
        windowSizeSqft: CONFIG.windowEstimates.sizesSqft[field('windowSize').value] || CONFIG.windowEstimates.sizesSqft.typical,
        patioDoors: Number.parseInt(field('patioDoors').value, 10),
        windowPerformance: field('windowPerformance').value,
        exposedWalls: field('exposedWalls').value,
        ceilingBoundary: field('ceilingBoundary').value,
        floorBoundary: field('floorBoundary').value,
        occupants: Number.parseInt(field('occupants').value, 10),
        airtightness: field('airtightness').value,
        kitchen: field('kitchen').value,
        sunroom: field('sunroom').value === 'yes'
      };
    }

    function updateSummary() {
      const roomType = field('roomType') ? field('roomType').value : '';
      const roomReady = Boolean(roomType) && state.roomTouched;
      if (summary('room')) summary('room').textContent = roomReady ? roomLabels[roomType] || 'Other' : '';
      if (summary('size')) {
        summary('size').textContent = roomReady ? `${formatNumber(Number(field('sqft').value || 0))} sq. ft.` : '';
      }
      if (summary('ceiling')) {
        const ceiling = field('ceiling') ? field('ceiling').value : '';
        summary('ceiling').textContent = roomReady ? ceilingLabels[ceiling] || `${ceiling} ft` : '';
      }
      if (summary('location')) {
        const zip = field('zip') ? field('zip').value.trim() : '';
        const region = REGION_NAMES[state.climate.type] || 'Climate region detected';
        summary('location').textContent = state.locationResolved ? `ZIP ${zip} · ${region}` : '';
      }
      if (summary('conditions')) {
        const sun = sunLabels[field('sun').value] || 'Balanced light';
        const insulation = insulationLabels[field('insulation').value] || 'Average';
        const airtightness = airtightnessLabels[field('airtightness').value] || 'Average air leakage';
        summary('conditions').textContent = `${sun} · ${insulation} · ${airtightness}`;
      }
      if (summary('exposure')) {
        const exposedWalls = exposedWallsLabels[field('exposedWalls').value] || '2 exterior walls';
        const above = ceilingBoundaryLabels[field('ceilingBoundary').value] || 'Insulated attic above';
        const below = floorBoundaryLabels[field('floorBoundary').value] || 'Slab / insulated below';
        summary('exposure').textContent = `${exposedWalls} · ${above} · ${below}`;
      }
      if (summary('windows')) {
        const performance = windowPerformanceLabels[field('windowPerformance').value] || 'Double-pane';
        if (getWindowMethod() === 'count') {
          const calculation = getWindowCountCalculation();
          if (calculation.valid) {
            const windows = calculation.count === 1 ? 'window' : 'windows';
            const size = windowSizeLabels[calculation.sizeKey] || 'Typical';
            const patio = calculation.patioDoors > 0
              ? ` + ${formatNumber(calculation.patioDoors)} patio/sliding ${calculation.patioDoors === 1 ? 'door' : 'doors'}`
              : '';
            summary('windows').textContent = `${formatNumber(calculation.count)} ${size.toLowerCase()} ${windows}${patio} · ${formatNumber(
              calculation.total
            )} sq. ft. · ${performance}`;
          } else {
            summary('windows').textContent = `Window count details incomplete · ${performance}`;
          }
        } else {
          const area = Number.parseFloat(field('windowArea') ? field('windowArea').value : '');
          const areaCopy = Number.isFinite(area) ? `${formatNumber(area)} sq. ft.` : 'Area not added';
          const isSunroom = roomType === 'sunroom';
          const ratio = isSunroom
            ? CONFIG.factors.envelope.windowRatio.sunroomEstimate
            : CONFIG.factors.envelope.windowRatio.defaultEstimate;
          const source = state.windowAreaTouched
            ? 'Entered total'
            : `${isSunroom ? 'Auto Sunroom estimate' : 'Auto estimate'} (${Math.round(ratio * 100)}%)`;
          summary('windows').textContent = `${source} · ${areaCopy} · ${performance}`;
        }
      }
      if (summary('occupancy')) {
        const occupants = occupantLabels[field('occupants').value] || '1–2 people';
        const kitchen = kitchenLabels[field('kitchen').value] || 'No kitchen';
        summary('occupancy').textContent = `${occupants} · ${kitchen}`;
      }
    }

    function syncChoiceGroup(fieldName) {
      const input = field(fieldName);
      root.querySelectorAll(`[data-btu-choice-field="${fieldName}"]`).forEach((candidate) => {
        const selected = Boolean(input) && input.value === candidate.dataset.btuChoiceValue;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function getWindowMethod() {
      return field('windowMethod') && field('windowMethod').value === 'count' ? 'count' : 'area';
    }

    function getWindowSizeSqft() {
      const key = field('windowSize') ? field('windowSize').value : 'typical';
      return CONFIG.windowEstimates.sizesSqft[key] || CONFIG.windowEstimates.sizesSqft.typical;
    }

    function getWindowCountCalculation() {
      const countInput = field('windowCount');
      const patioInput = field('patioDoors');
      const countRaw = countInput ? countInput.value.trim() : '';
      const patioRaw = patioInput ? patioInput.value.trim() : '';
      const count = Number(countRaw);
      const patioDoors = Number(patioRaw);
      const sizeKey = field('windowSize') ? field('windowSize').value : '';
      const sizeValid = Object.prototype.hasOwnProperty.call(CONFIG.windowEstimates.sizesSqft, sizeKey);
      const sizeSqft = getWindowSizeSqft();
      const countValid =
        countRaw !== '' &&
        Number.isInteger(count) &&
        count >= 0 &&
        count <= CONFIG.windowEstimates.maximumWindows;
      const patioValid =
        patioRaw !== '' &&
        Number.isInteger(patioDoors) &&
        patioDoors >= 0 &&
        patioDoors <= CONFIG.windowEstimates.maximumPatioDoors;
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      const maximum = Number.isFinite(sqft) && sqft > 0 ? Math.floor(sqft * 0.6) : 0;
      const total = countValid && patioValid && sizeValid
        ? count * sizeSqft + patioDoors * CONFIG.windowEstimates.patioDoorSqft
        : NaN;
      const withinLimit = Number.isFinite(total) && total <= maximum;

      return {
        countInput,
        patioInput,
        countRaw,
        patioRaw,
        count,
        patioDoors,
        sizeKey,
        sizeSqft,
        sizeValid,
        countValid,
        patioValid,
        maximum,
        total,
        withinLimit,
        valid: countValid && patioValid && sizeValid && withinLimit
      };
    }

    function effectiveWindowArea() {
      if (getWindowMethod() === 'count') {
        const calculation = getWindowCountCalculation();
        if (calculation.valid) return calculation.total;
      }
      return Number.parseFloat(field('windowArea') ? field('windowArea').value : '');
    }

    function setWindowCountError(calculation, visible, message) {
      const limitInvalid = calculation.countValid && calculation.patioValid && calculation.sizeValid && !calculation.withinLimit;
      if (calculation.countInput) {
        calculation.countInput.setAttribute(
          'aria-invalid',
          visible && (!calculation.countValid || !calculation.sizeValid || limitInvalid) ? 'true' : 'false'
        );
      }
      if (calculation.patioInput) {
        calculation.patioInput.setAttribute(
          'aria-invalid',
          visible && (!calculation.patioValid || limitInvalid) ? 'true' : 'false'
        );
      }
      const messageElement = error('windowCount');
      if (!messageElement) return;
      if (message) messageElement.textContent = message;
      messageElement.hidden = !visible;
    }

    function updateWindowCountGuidance(calculation) {
      const copy = root.querySelector('[data-btu-window-count-copy]');
      if (!copy) return;
      if (!calculation.countValid || !calculation.patioValid || !calculation.sizeValid) {
        copy.textContent = `Enter the number of windows. Typical assumes about ${CONFIG.windowEstimates.sizesSqft.typical} sq. ft. per window.`;
        return;
      }

      const windowsLabel = calculation.count === 1 ? 'window' : 'windows';
      const doorsLabel = calculation.patioDoors === 1 ? 'patio door' : 'patio doors';
      const doorFormula = calculation.patioDoors > 0
        ? ` + ${calculation.patioDoors} ${doorsLabel} × ${CONFIG.windowEstimates.patioDoorSqft} sq. ft.`
        : '';
      copy.textContent = `${calculation.count} ${windowsLabel} × ${calculation.sizeSqft} sq. ft.${doorFormula} = ${formatNumber(
        calculation.total
      )} sq. ft. estimated glass area.`;
    }

    function validateWindowCount(options) {
      const settings = options || {};
      const calculation = getWindowCountCalculation();
      updateWindowCountGuidance(calculation);

      let message = '';
      if (!calculation.countValid) {
        message = `Enter a whole number of windows from 0 to ${CONFIG.windowEstimates.maximumWindows}.`;
      } else if (!calculation.patioValid) {
        message = `Enter a whole number of patio glass doors from 0 to ${CONFIG.windowEstimates.maximumPatioDoors}.`;
      } else if (!calculation.sizeValid) {
        message = 'Select an average window size.';
      } else if (!calculation.withinLimit) {
        message = `This equals ${formatNumber(calculation.total)} square feet, above the ${formatNumber(
          calculation.maximum
        )} square-foot planning limit for this room. Use Total glass area or reduce the count.`;
      }

      const messageElement = error('windowCount');
      const keepVisible = Boolean(messageElement) && !messageElement.hidden;
      setWindowCountError(calculation, !calculation.valid && (settings.showErrors || keepVisible), message);
      return calculation.valid;
    }

    function updateWindowMethodUI() {
      const countMode = getWindowMethod() === 'count';
      const areaPanel = root.querySelector('[data-btu-window-area-panel]');
      const countPanel = root.querySelector('[data-btu-window-count-panel]');
      if (areaPanel) areaPanel.hidden = countMode;
      if (countPanel) countPanel.hidden = !countMode;
      root.querySelectorAll('[data-btu-choice-field="windowMethod"]').forEach((button) => {
        button.setAttribute('aria-expanded', button.dataset.btuChoiceValue === getWindowMethod() ? 'true' : 'false');
      });

      if (countMode) {
        if (!state.windowSizeTouched) {
          field('windowSize').value = field('roomType').value === 'sunroom' ? 'large' : 'typical';
          syncChoiceGroup('windowSize');
        }
        setError('windowArea', false);
        validateWindowCount({ showErrors: false });
      } else {
        const calculation = getWindowCountCalculation();
        setWindowCountError(calculation, false);
        updateWindowAreaGuidance();
      }
    }

    function setChoice(choice) {
      const fieldName = choice.dataset.btuChoiceField;
      const value = choice.dataset.btuChoiceValue;
      const input = field(fieldName);
      if (!fieldName || !input) return;

      input.value = value;
      syncChoiceGroup(fieldName);
      state.hasInteracted = true;

      if (fieldName === 'roomType') {
        state.roomTouched = true;
        field('sunroom').value = value === 'sunroom' ? 'yes' : 'no';
        if (getWindowMethod() === 'count') {
          if (!state.windowSizeTouched) {
            field('windowSize').value = value === 'sunroom' ? 'large' : 'typical';
            syncChoiceGroup('windowSize');
          }
          validateWindowCount({ showErrors: false });
        } else if (!state.windowAreaTouched) updateWindowAreaPreset();
        else updateWindowAreaGuidance();
        const rooms = root.querySelector('.btu-choice-grid--rooms');
        if (rooms) rooms.classList.remove('is-invalid');
        setError('roomType', false);
      }

      if (fieldName === 'windowMethod') updateWindowMethodUI();
      if (fieldName === 'windowSize') {
        state.windowSizeTouched = true;
        validateWindowCount({ showErrors: false });
      }

      updateSummary();
      renderResult();
    }

    function updateWindowAreaPreset() {
      const input = field('windowArea');
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      if (getWindowMethod() !== 'area' || !input || !Number.isFinite(sqft) || sqft <= 0) return;
      const ratio =
        field('roomType').value === 'sunroom'
          ? CONFIG.factors.envelope.windowRatio.sunroomEstimate
          : CONFIG.factors.envelope.windowRatio.defaultEstimate;
      input.max = String(Math.floor(sqft * 0.6));
      input.value = String(Math.round(sqft * ratio));
      setError('windowArea', false);
      updateWindowAreaGuidance();
    }

    function updateWindowAreaGuidance() {
      const copy = root.querySelector('[data-btu-window-estimate-copy]');
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      if (!copy || !Number.isFinite(sqft) || sqft <= 0) return;

      const isSunroom = field('roomType').value === 'sunroom';
      const ratio = isSunroom
        ? CONFIG.factors.envelope.windowRatio.sunroomEstimate
        : CONFIG.factors.envelope.windowRatio.defaultEstimate;
      const estimate = Math.round(sqft * ratio);
      const ratioLabel = isSunroom ? 'editable Sunroom estimate' : 'residential planning assumption';
      const formula = `${formatNumber(sqft)} sq. ft. × ${Math.round(ratio * 100)}% ${ratioLabel} = ${formatNumber(
        estimate
      )} sq. ft.`;

      copy.textContent = state.windowAreaTouched
        ? `Using your entry. Automatic planning estimate: ${formula}`
        : `Estimated for you: ${formula}`;
    }

    function validateWindowArea(options) {
      const settings = options || {};
      const input = field('windowArea');
      const sqft = Number.parseFloat(field('sqft') ? field('sqft').value : '');
      const value = Number.parseFloat(input ? input.value : '');
      const maximum = Number.isFinite(sqft) ? Math.floor(sqft * 0.6) : 0;
      if (input && maximum >= 0) input.max = String(maximum);
      const valid = Boolean(input) && Number.isFinite(value) && value >= 0 && value <= maximum;
      setError('windowArea', !valid);
      if (!valid && settings.clamp && input && Number.isFinite(value) && maximum >= 0) {
        input.value = String(Math.round(clamp(value, 0, maximum)));
        setError('windowArea', true, `Adjusted to ${formatNumber(Number(input.value))} square feet, the 60% planning limit for this room.`);
        return true;
      }
      return valid;
    }

    function revealWindowAdvanced() {
      const advanced = root.querySelector('[data-btu-window-advanced]');
      if (!advanced || advanced.open) return;
      advanced.open = true;
      advanced.setAttribute('open', '');
    }

    function validateStep(step) {
      if (step === 1) {
        const roomType = field('roomType').value;
        const room = readRoom();
        const roomGrid = root.querySelector('.btu-choice-grid--rooms');
        if (!roomType) {
          if (roomGrid) roomGrid.classList.add('is-invalid');
          setError('roomType', true, 'Select a room type to continue.');
          const firstChoice = root.querySelector('[data-btu-choice-field="roomType"]');
          if (firstChoice) firstChoice.focus();
          return false;
        }
        if (!room) {
          const sqft = field('sqft');
          if (sqft) sqft.focus();
          return false;
        }
      }
      if (step === 2 && !resolveZip({ force: true })) {
        const zip = field('zip');
        if (zip) zip.focus();
        return false;
      }
      if (step === 3) {
        if (getWindowMethod() === 'count' && !validateWindowCount({ showErrors: true })) {
          revealWindowAdvanced();
          const calculation = getWindowCountCalculation();
          const invalidControl = !calculation.countValid || !calculation.sizeValid
            ? calculation.countInput
            : !calculation.patioValid
              ? calculation.patioInput
              : calculation.countInput;
          if (invalidControl) invalidControl.focus();
          return false;
        }
        if (getWindowMethod() === 'area' && !validateWindowArea()) {
          revealWindowAdvanced();
          const windowArea = field('windowArea');
          if (windowArea) windowArea.focus();
          return false;
        }
      }
      return true;
    }

    function showStep(step, options) {
      const settings = options || {};
      const nextStep = clamp(step, 1, 5);
      let activePanel = null;
      state.currentStep = nextStep;
      panels.forEach((panel) => {
        const active = Number.parseInt(panel.dataset.btuStepPanel, 10) === nextStep;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) activePanel = panel;
      });

      if (progressLabel) progressLabel.textContent = nextStep === 5 ? 'Complete' : `Step ${nextStep} of 4`;
      if (progressBar) progressBar.style.width = `${Math.min(nextStep, 4) * 25}%`;
      if (backButton) backButton.disabled = nextStep === 1;
      if (nextButton) {
        nextButton.hidden = nextStep === 5;
        nextButton.setAttribute('aria-label', nextStep === 4 ? 'Calculate recommendation' : 'Next step');
      }
      if (navigation) navigation.hidden = nextStep === 5;
      if (nextStep !== 5) {
        state.productRequestToken += 1;
        if (recommendations) recommendations.hidden = true;
        if (productTrack) {
          productTrack.setAttribute('aria-busy', 'false');
          resetProductCarousel();
        }
      }

      if (!settings.silent) {
        track('btu_calculator_step_viewed', { step: nextStep === 5 ? 'result' : nextStep });
        const focusTarget = activePanel && (activePanel.querySelector('h2') || activePanel);
        if (focusTarget) {
          focusTarget.setAttribute('tabindex', '-1');
          window.requestAnimationFrame(() => focusTarget.focus());
        }
      }
    }

    function setRecommendationPresentation(mode, calculatedLoad, maximumCapacity) {
      if (mode !== 'oversize') {
        if (recommendationsEyebrow) recommendationsEyebrow.textContent = originalRecommendationsEyebrow;
        if (recommendationsHeading) recommendationsHeading.textContent = originalRecommendationsHeading;
        if (recommendationsCopy) recommendationsCopy.textContent = originalRecommendationsCopy;
        return;
      }

      if (recommendationsEyebrow) recommendationsEyebrow.textContent = 'Largest single-zone reference';
      if (recommendationsHeading) recommendationsHeading.textContent = `${formatNumber(maximumCapacity)} BTU single-zone reference.`;
      if (recommendationsCopy) {
        recommendationsCopy.textContent = `Reference only: the estimated ${formatNumber(
          calculatedLoad
        )} BTU/h planning load exceeds the ${formatNumber(maximumCapacity)} BTU/h single-zone limit. A single ${formatNumber(
          maximumCapacity
        )} BTU/h system would not meet it.`;
      }
    }

    function productCards() {
      return productTrack ? Array.from(productTrack.querySelectorAll('[data-product-handle]')) : [];
    }

    function prefersReducedMotion() {
      return Boolean(scope.matchMedia && scope.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    function productCarouselStep() {
      const cards = productCards();
      if (!cards.length) return productTrack ? productTrack.clientWidth : 0;
      if (cards.length > 1) {
        const measuredStep = cards[1].offsetLeft - cards[0].offsetLeft;
        if (measuredStep > 0) return measuredStep;
      }
      if (!productTrack) return cards[0].offsetWidth;
      const computed = scope.getComputedStyle ? scope.getComputedStyle(productTrack) : null;
      const gap = computed ? Number.parseFloat(computed.columnGap || computed.gap || '0') : 0;
      return cards[0].offsetWidth + (Number.isFinite(gap) ? gap : 0);
    }

    function updateProductCarousel() {
      if (!productTrack) return;
      const cards = productCards();
      if (productCarousel) productCarousel.hidden = cards.length === 0;
      const maximumScroll = Math.max(0, productTrack.scrollWidth - productTrack.clientWidth);
      const canScroll = cards.length > 1 && maximumScroll > 2;
      if (productCarousel) productCarousel.classList.toggle('is-scrollable', canScroll);
      if (productCarouselControls) productCarouselControls.hidden = !canScroll;
      productTrack.classList.toggle('is-draggable', canScroll);
      productTrack.tabIndex = canScroll ? 0 : -1;

      if (productPrevious) productPrevious.disabled = !canScroll || productTrack.scrollLeft <= 2;
      if (productNext) productNext.disabled = !canScroll || productTrack.scrollLeft >= maximumScroll - 2;

      let nextStatus = '';
      if (cards.length === 1) {
        nextStatus = 'Showing 1 product.';
      } else if (cards.length > 1) {
        const trackBounds = productTrack.getBoundingClientRect();
        const viewportStart = trackBounds.left + 2;
        const viewportEnd = trackBounds.right - 2;
        const visibleIndexes = [];
        cards.forEach((card, index) => {
          const bounds = card.getBoundingClientRect();
          const start = bounds.left;
          const end = bounds.right;
          if (end > viewportStart && start < viewportEnd) visibleIndexes.push(index);
        });
        const first = visibleIndexes.length ? visibleIndexes[0] : 0;
        const last = visibleIndexes.length ? visibleIndexes[visibleIndexes.length - 1] : first;
        nextStatus = `Showing products ${first + 1}–${last + 1} of ${cards.length}.`;
      }
      if (productCarouselStatus && nextStatus !== carouselStatusText) {
        carouselStatusText = nextStatus;
        productCarouselStatus.textContent = nextStatus;
      }
    }

    function scheduleProductCarouselUpdate(expectedRequestToken) {
      if (!productTrack) return;
      if (carouselFrame) scope.cancelAnimationFrame(carouselFrame);
      carouselFrame = scope.requestAnimationFrame(() => {
        carouselFrame = 0;
        if (expectedRequestToken != null && expectedRequestToken !== state.productRequestToken) return;
        if (state.currentStep !== 5 || !recommendations || recommendations.hidden) return;
        updateProductCarousel();
      });
    }

    function resetProductCarousel() {
      if (!productTrack) return;
      if (carouselFrame) {
        scope.cancelAnimationFrame(carouselFrame);
        carouselFrame = 0;
      }
      productTrack.scrollLeft = 0;
      productTrack.tabIndex = -1;
      productTrack.classList.remove('is-draggable', 'is-dragging');
      if (productCarousel) {
        productCarousel.hidden = true;
        productCarousel.classList.remove('is-scrollable');
      }
      if (productCarouselControls) productCarouselControls.hidden = true;
      if (productPrevious) productPrevious.disabled = true;
      if (productNext) productNext.disabled = true;
      if (productCarouselStatus) productCarouselStatus.textContent = '';
      carouselStatusText = '';
      carouselDrag = null;
      carouselSuppressClick = false;
    }

    function scrollProductCarousel(direction) {
      if (!productTrack) return;
      const maximumScroll = Math.max(0, productTrack.scrollWidth - productTrack.clientWidth);
      const step = productCarouselStep();
      if (!step || maximumScroll <= 0) return;
      let target = productTrack.scrollLeft;
      if (direction === 'start') target = 0;
      else if (direction === 'end') target = maximumScroll;
      else {
        const currentIndex = Math.round(productTrack.scrollLeft / step);
        target = (currentIndex + Number(direction)) * step;
      }
      productTrack.scrollTo({
        left: clamp(target, 0, maximumScroll),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function collectionContext(capacity) {
      const normalizedCapacity = CONFIG.capacityBinsBtu.includes(Number(capacity))
        ? Number(capacity)
        : CONFIG.limits.singleZoneAutomaticMaxBtu;
      const collectionHandle = `${normalizedCapacity}-btu-mini-split`;
      const configuredBase = String(root.dataset.productCollectionsUrl || '/collections').trim() || '/collections';
      const substitutedBase = configuredBase.replace(/\{(?:cap|capacity)\}/gi, String(normalizedCapacity));
      const baseUrl = new URL(substitutedBase, document.baseURI);
      baseUrl.search = '';
      baseUrl.hash = '';

      let collectionsPath = baseUrl.pathname.replace(/\/+$/, '');
      const collectionSegment = collectionsPath.lastIndexOf('/collections');
      if (collectionSegment >= 0) {
        collectionsPath = `${collectionsPath.slice(0, collectionSegment)}/collections`;
      } else {
        collectionsPath = `${collectionsPath}/collections`.replace(/\/{2,}/g, '/');
      }

      const collectionUrl = new URL(baseUrl.toString());
      collectionUrl.pathname = `${collectionsPath}/${collectionHandle}`.replace(/\/{2,}/g, '/');
      const productsUrl = new URL(collectionUrl.toString());
      productsUrl.pathname = `${collectionUrl.pathname}/products.json`.replace(/\/{2,}/g, '/');
      productsUrl.searchParams.set('limit', '50');

      const localePath = collectionsPath.slice(0, Math.max(0, collectionsPath.lastIndexOf('/collections')));
      const productsBaseUrl = new URL(baseUrl.toString());
      productsBaseUrl.pathname = `${localePath}/products`.replace(/\/{2,}/g, '/') || '/products';

      return { capacity: normalizedCapacity, collectionHandle, collectionUrl, productsUrl, productsBaseUrl };
    }

    function productUrl(product, variant, context) {
      const url = new URL(context.productsBaseUrl.toString());
      url.pathname = `${url.pathname.replace(/\/+$/, '')}/${encodeURIComponent(product.handle)}`;
      url.searchParams.set('variant', String(variant.id));
      return url.toString();
    }

    function availableCollectionProducts(context) {
      const cacheKey = context.productsUrl.toString();
      const cachedRequest = productCollectionRequests.get(cacheKey);
      if (cachedRequest) return cachedRequest;

      const request = fetch(cacheKey, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product collection request failed with ${response.status}.`);
          return response.json();
        })
        .then((payload) => {
          const products = payload && Array.isArray(payload.products) ? payload.products : [];
          const seenHandles = new Set();
          const matches = [];

          products.forEach((product) => {
            const handle = String(product && product.handle ? product.handle : '').trim();
            const tags = Array.isArray(product && product.tags)
              ? product.tags.map((tag) => String(tag).trim())
              : String(product && product.tags ? product.tags : '')
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean);
            if (
              !handle ||
              seenHandles.has(handle) ||
              String(product.vendor || '').trim() !== 'DELLA' ||
              String(product.product_type || '').trim() !== 'Mini Split' ||
              !tags.includes('Mini Split - Single Zone')
            ) {
              return;
            }

            const variants = Array.isArray(product.variants) ? product.variants : [];
            const variant = variants.find((candidate) => candidate && candidate.available === true);
            if (!variant) return;
            seenHandles.add(handle);
            matches.push({ product, variant });
          });

          return matches;
        });

      productCollectionRequests.set(cacheKey, request);
      request.catch(() => {
        if (productCollectionRequests.get(cacheKey) === request) productCollectionRequests.delete(cacheKey);
      });
      return request;
    }

    function formatMoney(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      const currency = String(root.dataset.currencyCode || 'USD').trim().toUpperCase() || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency
        }).format(amount);
      } catch (_error) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }

    function productImageUrl(product) {
      const images = Array.isArray(product && product.images) ? product.images : [];
      const image = images.find((candidate) => candidate && (candidate.src || candidate.url));
      return String(
        (image && (image.src || image.url)) ||
          (product && product.featured_image && (product.featured_image.src || product.featured_image)) ||
          root.dataset.productPlaceholderImage ||
          ''
      ).trim();
    }

    function productSeries(title) {
      const normalizedTitle = String(title || '');
      const seriesMatch = normalizedTitle.match(/(?:DELLA\s+)?([A-Za-z]+)\s+Series\b/i);
      return seriesMatch ? `${seriesMatch[1].toUpperCase()} SERIES` : 'DELLA MINI SPLIT';
    }

    function createProductCard(item, context, mode) {
      const { product, variant } = item;
      const href = productUrl(product, variant, context);
      const title = String(product.title || 'DELLA mini split');
      const card = document.createElement('article');
      card.className = 'btu-product-card';
      card.dataset.productHandle = String(product.handle || '');
      card.dataset.productTitle = title;
      card.dataset.productCapacity = String(context.capacity);

      const imageLink = document.createElement('a');
      imageLink.className = 'btu-product-card__image';
      imageLink.href = href;
      imageLink.setAttribute('aria-label', `View ${title}`);
      const imageUrl = productImageUrl(product);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = title;
        image.width = 424;
        image.height = 424;
        image.loading = 'lazy';
        image.draggable = false;
        imageLink.appendChild(image);
      }
      card.appendChild(imageLink);

      const series = document.createElement('p');
      series.className = 'btu-product-card__series';
      series.textContent = productSeries(title);
      card.appendChild(series);

      const heading = document.createElement('h3');
      const titleLink = document.createElement('a');
      titleLink.href = href;
      titleLink.textContent = title;
      heading.appendChild(titleLink);
      card.appendChild(heading);

      const meta = document.createElement('p');
      meta.className = 'btu-product-card__meta';
      meta.textContent = mode === 'oversize'
        ? 'Reference only — this unit alone does not meet the estimated load.'
        : `Matched to your ${formatNumber(context.capacity)} BTU estimated nominal size.`;
      card.appendChild(meta);

      const price = document.createElement('p');
      price.className = 'btu-product-card__price';
      price.textContent = formatMoney(variant.price);
      const currentPrice = Number(variant.price);
      const compareAtPrice = Number(variant.compare_at_price);
      if (Number.isFinite(compareAtPrice) && compareAtPrice > currentPrice) {
        const compare = document.createElement('s');
        compare.textContent = formatMoney(compareAtPrice);
        price.appendChild(compare);
      }
      card.appendChild(price);
      return card;
    }

    function setProductFallback(visible, context) {
      if (!productFallback) return;
      productFallback.hidden = !visible;
      const fallbackLink = productFallback.querySelector('a');
      if (!fallbackLink || !context) return;
      fallbackLink.href = context.collectionUrl.toString();
      fallbackLink.textContent = `Browse all ${formatNumber(context.capacity)} BTU systems`;
    }

    function updateMaximumProductReference(item, context) {
      if (!item || !resultBody) return;
      const details = resultBody.querySelector('[data-btu-maximum-product-details]');
      const actions = resultBody.querySelector('[data-btu-maximum-product-actions]');
      if (details) details.textContent = String(item.product.title || '');
      if (actions) {
        const existingLink = actions.querySelector('[data-btu-maximum-product-link]');
        if (existingLink) existingLink.remove();
        const link = document.createElement('a');
        link.className = 'r-cta';
        link.href = productUrl(item.product, item.variant, context);
        link.dataset.btuMaximumProductLink = '';
        link.textContent = `View ${formatNumber(context.capacity)} BTU model`;
        actions.prepend(link);
      }
    }

    async function loadRecommendedProducts(capacity, mode) {
      if (!recommendations || !productTrack) return [];
      const context = collectionContext(capacity);
      const requestToken = ++state.productRequestToken;
      const configuredLimit = Number.parseInt(root.dataset.productLimit || '6', 10);
      const visibleLimit = clamp(Number.isFinite(configuredLimit) ? configuredLimit : 6, 1, 20);
      productTrack.replaceChildren();
      resetProductCarousel();
      productTrack.setAttribute('aria-busy', 'true');
      setProductFallback(false, context);
      if (productStatus) {
        productStatus.hidden = false;
        productStatus.classList.add('is-loading');
        productStatus.textContent = `Loading ${formatNumber(context.capacity)} BTU products…`;
      }
      if (productViewAll) {
        productViewAll.href = context.collectionUrl.toString();
        productViewAll.textContent = `View all ${formatNumber(context.capacity)} BTU systems`;
        productViewAll.hidden = false;
      }
      recommendations.hidden = state.currentStep !== 5;

      try {
        const matches = await availableCollectionProducts(context);
        if (requestToken !== state.productRequestToken || state.currentStep !== 5) return [];
        const visibleProducts = matches.slice(0, visibleLimit);
        productTrack.replaceChildren(...visibleProducts.map((item) => createProductCard(item, context, mode)));
        productTrack.setAttribute('aria-busy', 'false');
        scheduleProductCarouselUpdate(requestToken);
        setProductFallback(visibleProducts.length === 0, context);
        if (productStatus) {
          productStatus.classList.remove('is-loading');
          productStatus.textContent = visibleProducts.length
            ? `Showing ${visibleProducts.length} available ${formatNumber(context.capacity)} BTU ${visibleProducts.length === 1 ? 'system' : 'systems'}.`
            : `No available ${formatNumber(context.capacity)} BTU single-zone products were found. View the full collection for current options.`;
          productStatus.hidden = true;
        }
        if (mode === 'oversize' && visibleProducts[0]) updateMaximumProductReference(visibleProducts[0], context);
        return visibleProducts;
      } catch (_error) {
        if (requestToken !== state.productRequestToken || state.currentStep !== 5) return [];
        productTrack.replaceChildren();
        resetProductCarousel();
        productTrack.setAttribute('aria-busy', 'false');
        setProductFallback(true, context);
        if (productStatus) {
          productStatus.classList.remove('is-loading');
          productStatus.textContent = `Products could not be loaded right now. View the ${formatNumber(context.capacity)} BTU collection instead.`;
          productStatus.hidden = true;
        }
        return [];
      }
    }

    function buildFactorCards(room, result) {
      const climateCopy = state.locationResolved
        ? `Summer cooling: about ${result.summerOutdoor}°F outdoors.<br>Winter heating: about ${result.winterOutdoor}°F outdoors.`
        : 'General moderate climate estimate.';
      const glassCopy = room.windowMethod === 'count'
        ? `${formatNumber(result.windowArea)} sq. ft. estimated from ${formatNumber(room.windowCount)} windows at ${formatNumber(
            room.windowSizeSqft
          )} sq. ft. each${room.patioDoors > 0 ? ` plus ${formatNumber(room.patioDoors)} patio glass ${room.patioDoors === 1 ? 'door' : 'doors'}` : ''}`
        : `${formatNumber(result.windowArea)} sq. ft. of glass`;
      return `<div class="r-factors">
        <div><b>Room volume</b><span>${formatNumber(room.sqft)} sq. ft. with a ${room.ceiling} ft ceiling.</span></div>
        <div><b>Regional climate</b><span>${climateCopy}</span></div>
        <div><b>Envelope</b><span>${insulationLabels[room.insulation] || 'Average'} insulation, ${glassCopy} and ${room.airtightness || 'average'} air leakage.</span></div>
      </div>`;
    }

    function renderResult() {
      if (!resultBody) return null;
      const room = readRoom();
      if (!room) return null;
      const result = calculateRoomLoad(room, state.climate);
      const sizing = selectCapacity(result.point);
      const whyItems = buildWhyItems(result, room, sizing);
      const roomLabel = roomLabels[room.roomType] || 'room';
      const calculatedLoad = roundLoad(result.point);
      const coolingLoad = roundLoad(result.cooling);
      const heatingLoad = roundLoad(result.heating);
      const governingLabel = result.governing === 'heating' ? 'Heating' : 'Cooling';
      const whyId = `BtuCalculator-${sectionId}-why`;
      const recommendationId = `BtuCalculator-${sectionId}-recommendations`;
      const maximumSingleZoneCapacity = CONFIG.limits.singleZoneAutomaticMaxBtu;
      const multiZoneUrl = escapeHtml(root.dataset.multiZoneUrl || '/collections/mini-split-multi-zone');
      let markup;
      let productCapacity;
      let productMode;

      setRecommendationPresentation('normal');
      if (sizing.kind === 'professional' || sizing.kind === 'multi') {
        const beyondAutomaticRange = sizing.kind === 'professional'
          ? 'This simplified result is beyond DELLA’s automatic planning range.'
          : 'This simplified result is above DELLA’s single-zone range.';
        markup = `<p class="r-cap">Your estimated room load</p>
          <p class="r-big"><span>${formatNumber(calculatedLoad)}</span> <small>BTU/h</small></p>
          <p class="r-best">${governingLabel} is the governing planning load for your ${formatNumber(room.sqft)} sq. ft. ${roomLabel.toLowerCase()}.</p>
          <p class="r-loads"><b>${governingLabel} governs:</b> ${formatNumber(coolingLoad)} BTU/h cooling · ${formatNumber(heatingLoad)} BTU/h heating.</p>
          ${result.governing === 'heating' ? `<p class="r-governing-note">Heating output can fall as outdoor temperature drops. Verify the selected multi-zone combination near ${result.winterOutdoor}°F.</p>` : ''}
          <p class="r-maximum-model"><b>Largest DELLA single-zone capacity: ${formatNumber(
            maximumSingleZoneCapacity
          )} BTU/h</b><span data-btu-maximum-product-details></span></p>
          <div class="r-warning">${beyondAutomaticRange} One ${formatNumber(
            maximumSingleZoneCapacity
          )} BTU/h single-zone unit cannot meet this estimated load by itself. Multiple systems or a multi-zone design may be needed. Obtain a room-by-room Manual J calculation using ACCA-approved software and verify manufacturer performance before purchase.</div>
          <div class="r-actions" data-btu-maximum-product-actions>
            <a class="r-cta r-cta--secondary" href="${multiZoneUrl}" data-btu-multi-zone-link>Explore multi-zone systems</a>
          </div>`;
        productCapacity = maximumSingleZoneCapacity;
        productMode = 'oversize';
        setRecommendationPresentation(productMode, calculatedLoad, productCapacity);
      } else {
        const capacity = sizing.capacity;
        const rangeMinimum = Math.max(0, roundLoad(result.lower));
        const rangeMaximum = roundLoad(result.upper);
        const whyList = whyItems.map((item) => `<li>${item}</li>`).join('');
        markup = `<p class="r-cap">Your estimated nominal size</p>
          <p class="r-big"><span>${formatNumber(capacity)}</span> <small>BTU/h</small></p>
          <p class="r-best">Planning estimate for your ${formatNumber(room.sqft)} sq. ft. ${roomLabel.toLowerCase()}.</p>
          <p class="r-loads"><b>${governingLabel} governs:</b> ${formatNumber(coolingLoad)} BTU/h cooling · ${formatNumber(heatingLoad)} BTU/h heating.</p>
          ${result.governing === 'heating' ? `<p class="r-governing-note">Heating output can fall as outdoor temperature drops. Verify this model's published heating capacity near ${result.winterOutdoor}°F before purchase.</p>` : ''}
          <div class="r-range" aria-label="Estimated load range from ${formatNumber(rangeMinimum)} to ${formatNumber(rangeMaximum)} BTU per hour">
            <div class="r-range__bar"><i></i><i></i><i></i><b style="left:50%"></b></div>
            <div class="r-range__labels"><span>Lower estimate</span><span>Central estimate</span><span>Upper estimate</span></div>
          </div>
          ${buildFactorCards(room, result)}
          <a class="r-cta" href="#${recommendationId}" data-btu-see-recommendations data-btu-capacity="${capacity}">${root.dataset.matchingProductsLabel || 'See systems in this size'}</a>
          <details class="r-details" id="${whyId}"><summary>Why this size?</summary><ul>${whyList}</ul></details>`;
        productCapacity = capacity;
        productMode = 'normal';
      }

      resultBody.innerHTML = markup;
      state.result = { room, result, sizing, calculatedLoad };
      if (state.currentStep === 5) loadRecommendedProducts(productCapacity, productMode);
      return state.result;
    }

    function announceCompletion(calculation) {
      if (!calculation) return;
      const signature = [
        CONFIG.engineVersion,
        calculation.calculatedLoad,
        calculation.sizing.capacity || 'review',
        calculation.sizing.kind,
        state.climate.type,
        calculation.result.governing
      ].join('|');
      if (signature === state.lastCompletionSignature) return;
      state.lastCompletionSignature = signature;
      track('btu_calculator_completed', {
        calculator_type: 'single_room',
        engine_version: CONFIG.engineVersion,
        calculated_load_btu: calculation.calculatedLoad,
        cooling_load_btu: roundLoad(calculation.result.cooling),
        heating_load_btu: roundLoad(calculation.result.heating),
        governing_load: calculation.result.governing,
        recommended_capacity_btu: calculation.sizing.capacity,
        climate: state.climate.type,
        window_input_method: calculation.room.windowMethod
      });
    }

    function completeCalculator() {
      showStep(5);
      state.productRequestToken += 1;
      if (recommendations) recommendations.hidden = true;
      if (resultBody) {
        resultBody.innerHTML = '<div class="r-analyzing"><span></span><p>Analyzing your space</p><small>Matching room, regional climate and envelope conditions.</small></div>';
      }
      if (state.completionTimer) window.clearTimeout(state.completionTimer);
      state.completionTimer = window.setTimeout(() => {
        const calculation = renderResult();
        announceCompletion(calculation);
        state.completionTimer = null;
      }, 550);
    }

    if (productTrack) {
      productTrack.addEventListener('scroll', () => scheduleProductCarouselUpdate(), { passive: true });
      productTrack.addEventListener('keydown', (event) => {
        if (event.target !== productTrack) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollProductCarousel(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollProductCarousel(1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          scrollProductCarousel('start');
        } else if (event.key === 'End') {
          event.preventDefault();
          scrollProductCarousel('end');
        }
      });
      productTrack.addEventListener('dragstart', (event) => event.preventDefault());
      productTrack.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !productTrack.classList.contains('is-draggable')) return;
        carouselDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: productTrack.scrollLeft,
          moved: false
        };
        productTrack.setPointerCapture(event.pointerId);
      });
      productTrack.addEventListener('pointermove', (event) => {
        if (!carouselDrag || carouselDrag.pointerId !== event.pointerId) return;
        const distance = event.clientX - carouselDrag.startX;
        if (!carouselDrag.moved && Math.abs(distance) > 5) {
          carouselDrag.moved = true;
          productTrack.classList.add('is-dragging');
        }
        if (!carouselDrag.moved) return;
        event.preventDefault();
        productTrack.scrollLeft = carouselDrag.startScrollLeft - distance;
      });

      const finishCarouselDrag = (event, cancelled) => {
        if (!carouselDrag || carouselDrag.pointerId !== event.pointerId) return;
        const moved = carouselDrag.moved;
        carouselDrag = null;
        productTrack.classList.remove('is-dragging');
        if (productTrack.hasPointerCapture(event.pointerId)) productTrack.releasePointerCapture(event.pointerId);
        if (moved && !cancelled) {
          const step = productCarouselStep();
          const maximumScroll = Math.max(0, productTrack.scrollWidth - productTrack.clientWidth);
          const target = step ? Math.round(productTrack.scrollLeft / step) * step : productTrack.scrollLeft;
          carouselSuppressClick = true;
          productTrack.scrollTo({
            left: clamp(target, 0, maximumScroll),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
          scope.setTimeout(() => {
            carouselSuppressClick = false;
          }, 150);
        }
      };

      productTrack.addEventListener('pointerup', (event) => finishCarouselDrag(event, false));
      productTrack.addEventListener('pointercancel', (event) => finishCarouselDrag(event, true));
      productTrack.addEventListener(
        'click',
        (event) => {
          if (!carouselSuppressClick || !event.target.closest('a')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          carouselSuppressClick = false;
        },
        true
      );

      if (productPrevious) productPrevious.addEventListener('click', () => scrollProductCarousel(-1));
      if (productNext) productNext.addEventListener('click', () => scrollProductCarousel(1));

      let carouselResizeObserver = null;
      let carouselResizeFallback = null;
      if (typeof scope.ResizeObserver === 'function') {
        carouselResizeObserver = new scope.ResizeObserver(() => scheduleProductCarouselUpdate());
        carouselResizeObserver.observe(productTrack);
      } else {
        carouselResizeFallback = () => scheduleProductCarouselUpdate();
        scope.addEventListener('resize', carouselResizeFallback, { passive: true });
      }

      const unloadCarousel = (event) => {
        if (event.target !== root && !(event.target && event.target.contains && event.target.contains(root))) return;
        if (carouselResizeObserver) carouselResizeObserver.disconnect();
        if (carouselResizeFallback) scope.removeEventListener('resize', carouselResizeFallback);
        if (carouselFrame) scope.cancelAnimationFrame(carouselFrame);
        document.removeEventListener('shopify:section:unload', unloadCarousel);
      };
      document.addEventListener('shopify:section:unload', unloadCarousel);
      resetProductCarousel();
    }

    root.addEventListener('submit', (event) => {
      if (event.target.matches('[data-btu-form]')) event.preventDefault();
      if (event.target.matches('[data-btu-product-form]')) {
        const card = event.target.closest('[data-product-handle]');
        track('btu_product_add_to_cart', { product_handle: card ? card.dataset.productHandle : '' });
      }
    });

    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-btu-field="zip"]')) {
        event.target.value = event.target.value.replace(/\D/g, '').slice(0, 5);
        state.hasInteracted = true;
        if (event.target.value !== state.lastResolvedZip) {
          state.lastResolvedZip = '';
          state.locationResolved = false;
        }
        if (event.target.value.length < 5) {
          setError('zip', false);
          updateSummary();
        } else {
          resolveZip();
        }
        return;
      }

      if (event.target.matches('[data-btu-field="sqft"]')) {
        state.hasInteracted = true;
        state.roomTouched = Boolean(field('roomType').value);
        const sqft = Number.parseFloat(event.target.value);
        const windowArea = field('windowArea');
        if (windowArea && Number.isFinite(sqft) && sqft > 0) windowArea.max = String(Math.floor(sqft * 0.6));
        if (getWindowMethod() === 'count') {
          validateWindowCount({ showErrors: false });
        } else if (!state.windowAreaTouched) {
          updateWindowAreaPreset();
        } else {
          updateWindowAreaGuidance();
        }
        updateSummary();
        renderResult();
        return;
      }

      if (event.target.matches('[data-btu-field="windowArea"]')) {
        state.hasInteracted = true;
        state.windowAreaTouched = true;
        setError('windowArea', false);
        updateWindowAreaGuidance();
        updateSummary();
        renderResult();
        return;
      }

      if (event.target.matches('[data-btu-field="windowCount"], [data-btu-field="patioDoors"]')) {
        state.hasInteracted = true;
        validateWindowCount({ showErrors: false });
        updateSummary();
        renderResult();
      }
    });

    root.addEventListener(
      'blur',
      (event) => {
        if (event.target.matches('[data-btu-field="zip"]')) resolveZip();
        if (event.target.matches('[data-btu-field="windowArea"]')) {
          validateWindowArea({ clamp: true });
          updateWindowAreaGuidance();
          updateSummary();
          renderResult();
        }
        if (event.target.matches('[data-btu-field="windowCount"], [data-btu-field="patioDoors"]')) {
          validateWindowCount({ showErrors: true });
          updateSummary();
          renderResult();
        }
      },
      true
    );

    root.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-btu-choice-field]');
      if (choice && root.contains(choice)) {
        setChoice(choice);
        return;
      }

      const start = event.target.closest('[data-btu-start]');
      if (start && root.contains(start)) {
        track('btu_calculator_started');
        return;
      }

      const back = event.target.closest('[data-btu-back]');
      if (back && root.contains(back)) {
        showStep(state.currentStep - 1);
        return;
      }

      const next = event.target.closest('[data-btu-next]');
      if (next && root.contains(next)) {
        if (!validateStep(state.currentStep)) return;
        track('btu_calculator_step_completed', { step: state.currentStep });
        if (state.currentStep === 4) completeCalculator();
        else showStep(state.currentStep + 1);
        return;
      }

      const resultLink = event.target.closest('[data-btu-see-recommendations]');
      if (resultLink && root.contains(resultLink)) {
        track('btu_matching_product_clicked', {
          query: `${resultLink.dataset.btuCapacity || ''} BTU mini split`.trim()
        });
        return;
      }

      const productLink = event.target.closest('[data-btu-product-track] a');
      if (productLink && root.contains(productLink)) {
        const card = productLink.closest('[data-product-handle]');
        track('btu_product_clicked', { product_handle: card ? card.dataset.productHandle : '' });
      }
    });

    root.querySelectorAll('[data-btu-choice-field]').forEach((choice) => {
      const input = field(choice.dataset.btuChoiceField);
      const selected = Boolean(input) && input.value === choice.dataset.btuChoiceValue;
      choice.classList.toggle('is-selected', selected);
      choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    setClimateChip(state.climate, 'default');
    updateWindowAreaPreset();
    updateWindowMethodUI();
    updateSummary();
    renderResult();
    showStep(1, { silent: true });
  }

  function initialize(scopeElement) {
    const candidates = [];
    if (scopeElement && scopeElement.matches && scopeElement.matches('[data-btu-calculator-section]')) {
      candidates.push(scopeElement);
    }
    if (scopeElement && scopeElement.querySelectorAll) {
      candidates.push(...scopeElement.querySelectorAll('[data-btu-calculator-section]'));
    }

    candidates.forEach((root) => {
      if (initializedRoots.has(root)) return;
      initializedRoots.add(root);
      createCalculator(root);
    });
  }

  scope.DELLA_BTU_CALCULATOR = Object.freeze({
    engineVersion: CONFIG.engineVersion,
    calculateRoomLoad,
    selectCapacity,
    resolveZipRecord,
    initialize,
    config: CONFIG,
    stateClimate: STATE_CLIMATE
  });

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initialize(document));
    } else {
      initialize(document);
    }
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  }
})(typeof window !== 'undefined' ? window : globalThis);
